import { useState, useRef, useCallback, useEffect } from 'react';
import { RecordingState, AudioRefs } from '../types';
import { pickMimeType } from '../../../../utils/audio';
import { requestWakeLock, releaseWakeLock } from '../../../../utils/wake-lock';
import { createAudio, destroyAudio } from '../../../../helpers/create-audio';
import { isIos } from '../../../../platform';
import { emitPermissionAlert } from '../../../../contexts/permission-alert-context';

const MAX_RECORDING_DURATION = 30 * 60; // 30 minutes in seconds

export const useAudioRecording = (onError?: (msg: string) => void, onStreamReady?: (stream: MediaStream) => void) => {
  const notifyError = useCallback((msg: string) => {
    if (onError) onError(msg);
    else console.error(msg);
  }, [onError]);
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    recordingTime: 0,
    recordedAudioURL: null,
    isPlayingRecording: false,
    audioDuration: 0,
    currentPlayTime: 0,
  });
  const [maxDurationReached, setMaxDurationReached] = useState(false);

  // Track whether recording was auto-paused due to visibility change
  // (as opposed to user-initiated pause). Only auto-paused recordings
  // should auto-resume when the user returns.
  const pausedByVisibilityRef = useRef(false);

  // Screen wake lock — keeps device awake while recording
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const refs: AudioRefs = {
    mediaRecorderRef: useRef<MediaRecorder | null>(null),
    audioChunksRef: useRef<Blob[]>([]),
    streamRef: useRef<MediaStream | null>(null),
    intervalRef: useRef<NodeJS.Timeout | null>(null),
    audioRef: useRef<HTMLAudioElement | null>(null),
    playbackIntervalRef: useRef<NodeJS.Timeout | null>(null),
  };

  const updateRecordingState = useCallback((updates: Partial<RecordingState>) => {
    setRecordingState((prev) => ({ ...prev, ...updates }));
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      refs.streamRef.current = stream;
      onStreamReady?.(stream);

      const mimeType = pickMimeType();

      if (!mimeType) {
        throw new Error('No supported audio format found');
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      refs.audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          refs.audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(refs.audioChunksRef.current, { type: mimeType });
        const audioURL = URL.createObjectURL(audioBlob);
        updateRecordingState({ recordedAudioURL: audioURL });
      };

      refs.mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);

      // Keep screen awake while recording
      requestWakeLock().then((lock) => { wakeLockRef.current = lock; });

      updateRecordingState({
        isRecording: true,
        isPaused: false,
        recordingTime: 0,
      });

      refs.intervalRef.current = setInterval(() => {
        setRecordingState((prev) => {
          const newRecordingTime = prev.recordingTime + 1;

          // Auto-stop at 10 minutes
          if (newRecordingTime >= MAX_RECORDING_DURATION) {
            // Stop the recording automatically
            setTimeout(() => {
              if (
                refs.mediaRecorderRef.current &&
                refs.mediaRecorderRef.current.state === 'recording'
              ) {
                refs.mediaRecorderRef.current.stop();
                setMaxDurationReached(true);
              }
            }, 0);
          }

          return {
            ...prev,
            recordingTime: newRecordingTime,
          };
        });
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      if (isIos()) {
        emitPermissionAlert('microphone');
      // } else if (isNative()) {
      //   emitPermissionAlert('microphone');
      } else {
        notifyError('Could not access microphone. Please check permissions.');
      }
    }
  }, [updateRecordingState, notifyError, onStreamReady]);

  const pauseRecording = useCallback(() => {
    if (refs.mediaRecorderRef.current && refs.mediaRecorderRef.current.state === 'recording') {
      refs.mediaRecorderRef.current.pause();
      updateRecordingState({ isPaused: true });
      if (refs.intervalRef.current) {
        clearInterval(refs.intervalRef.current);
      }
      // User-initiated pause — don't auto-resume on visibility change
      pausedByVisibilityRef.current = false;
    }
  }, [updateRecordingState]);

  const resumeRecording = useCallback(() => {
    if (refs.mediaRecorderRef.current && refs.mediaRecorderRef.current.state === 'paused') {
      refs.mediaRecorderRef.current.resume();
      updateRecordingState({ isPaused: false });

      refs.intervalRef.current = setInterval(() => {
        setRecordingState((prev) => ({
          ...prev,
          recordingTime: prev.recordingTime + 1,
        }));
      }, 1000);
    }
  }, [updateRecordingState]);

  const stopRecording = useCallback(() => {
    if (refs.mediaRecorderRef.current) {
      refs.mediaRecorderRef.current.stop();
    }
    if (refs.streamRef.current) {
      refs.streamRef.current.getTracks().forEach((track) => track.stop());
      refs.streamRef.current = null;
    }
    if (refs.intervalRef.current) {
      clearInterval(refs.intervalRef.current);
      refs.intervalRef.current = null;
    }
    releaseWakeLock(wakeLockRef.current);
    wakeLockRef.current = null;
    updateRecordingState({ isRecording: false, isPaused: false });
  }, [updateRecordingState]);

  const playRecordedAudio = useCallback(() => {
    if (recordingState.recordedAudioURL) {
      if (refs.audioRef.current) {
        destroyAudio(refs.audioRef.current);
        refs.audioRef.current = null;
      }

      const audio = createAudio();
      refs.audioRef.current = audio;

      audio.onloadedmetadata = () => {
        if (isFinite(audio.duration) && audio.duration > 0) {
          updateRecordingState({ audioDuration: audio.duration });
        } else {
          updateRecordingState({ audioDuration: recordingState.recordingTime });
        }
      };

      audio.onplay = () => {
        updateRecordingState({ isPlayingRecording: true });

        refs.playbackIntervalRef.current = setInterval(() => {
          if (
            refs.audioRef.current &&
            !refs.audioRef.current.paused &&
            !refs.audioRef.current.ended
          ) {
            const currentTime = refs.audioRef.current.currentTime;
            updateRecordingState({ currentPlayTime: currentTime });

            if (
              (!recordingState.audioDuration || !isFinite(recordingState.audioDuration)) &&
              isFinite(refs.audioRef.current.duration) &&
              refs.audioRef.current.duration > 0
            ) {
              updateRecordingState({ audioDuration: refs.audioRef.current.duration });
            }
          }
        }, 100);
      };

      audio.onended = () => {
        updateRecordingState({ isPlayingRecording: false, currentPlayTime: 0 });
        if (refs.playbackIntervalRef.current) {
          clearInterval(refs.playbackIntervalRef.current);
          refs.playbackIntervalRef.current = null;
        }
      };

      audio.onpause = () => {
        updateRecordingState({ isPlayingRecording: false });
        if (refs.playbackIntervalRef.current) {
          clearInterval(refs.playbackIntervalRef.current);
          refs.playbackIntervalRef.current = null;
        }
      };

      audio.onerror = (error) => {
        console.error('Audio playback error:', error, audio.error);
        updateRecordingState({
          isPlayingRecording: false,
          audioDuration: recordingState.recordingTime,
        });
        notifyError('Error playing audio. The recording format may not be supported.');
      };

      audio.src = recordingState.recordedAudioURL;
      audio.load();

      setTimeout(() => {
        audio.play().catch((error) => {
          console.error('Error playing audio:', error);
          updateRecordingState({ isPlayingRecording: false });
          notifyError('Could not play audio. Please try re-recording.');
        });
      }, 100);
    }
  }, [
    recordingState.recordedAudioURL,
    recordingState.recordingTime,
    recordingState.audioDuration,
    updateRecordingState,
    notifyError,
  ]);

  const pausePlayback = useCallback(() => {
    if (refs.audioRef.current) {
      refs.audioRef.current.pause();
    }
  }, []);

  const resumePlayback = useCallback(() => {
    if (refs.audioRef.current) {
      refs.audioRef.current.play().catch((error) => {
        console.error('Error resuming audio playback:', error);
        updateRecordingState({ isPlayingRecording: false });
      });
    }
  }, [updateRecordingState, refs.audioRef]);

  const cleanup = useCallback(() => {
    if (refs.intervalRef.current) {
      clearInterval(refs.intervalRef.current);
    }
    if (refs.playbackIntervalRef.current) {
      clearInterval(refs.playbackIntervalRef.current);
    }
    if (refs.streamRef.current) {
      refs.streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (recordingState.recordedAudioURL) {
      URL.revokeObjectURL(recordingState.recordedAudioURL);
    }
    if (refs.audioRef.current) {
      refs.audioRef.current.pause();
    }
    releaseWakeLock(wakeLockRef.current);
    wakeLockRef.current = null;
  }, [recordingState.recordedAudioURL]);

  const getRecordedAudioBlob = useCallback((): Blob | null => {
    try {
      const chunks = refs.audioChunksRef.current || [];
      if (chunks.length === 0) return null;
      return new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
    } catch (err) {
      console.error('Error building recorded audio blob:', err);
      return null;
    }
  }, []);

  // Auto-pause recording when the user leaves the app/tab, auto-resume on return.
  // This prevents the OS from killing the audio stream while backgrounded and
  // avoids triggering splash-screen logic from background auth refreshes.
  useEffect(() => {
    const onVisibilityChange = () => {
      const recorder = refs.mediaRecorderRef.current;
      if (!recorder) return;

      if (document.hidden) {
        // Tab/app went to background — pause if actively recording
        if (recorder.state === 'recording') {
          recorder.pause();
          setRecordingState((prev) => ({ ...prev, isPaused: true }));
          if (refs.intervalRef.current) {
            clearInterval(refs.intervalRef.current);
            refs.intervalRef.current = null;
          }
          pausedByVisibilityRef.current = true;
        }
      } else {
        // Tab/app came back — resume only if WE paused it (not the user)
        if (recorder.state === 'paused' && pausedByVisibilityRef.current) {
          pausedByVisibilityRef.current = false;
          recorder.resume();
          setRecordingState((prev) => ({ ...prev, isPaused: false }));

          // Re-acquire wake lock (browser releases it when page goes hidden)
          requestWakeLock().then((lock) => { wakeLockRef.current = lock; });

          refs.intervalRef.current = setInterval(() => {
            setRecordingState((prev) => {
              const newRecordingTime = prev.recordingTime + 1;

              if (newRecordingTime >= MAX_RECORDING_DURATION) {
                setTimeout(() => {
                  if (
                    refs.mediaRecorderRef.current &&
                    refs.mediaRecorderRef.current.state === 'recording'
                  ) {
                    refs.mediaRecorderRef.current.stop();
                    setMaxDurationReached(true);
                  }
                }, 0);
              }

              return { ...prev, recordingTime: newRecordingTime };
            });
          }, 1000);
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Stop media stream and clear timers on unmount
  useEffect(() => () => {
    if (refs.intervalRef.current) clearInterval(refs.intervalRef.current);
    if (refs.playbackIntervalRef.current) clearInterval(refs.playbackIntervalRef.current);
    if (refs.streamRef.current) {
      refs.streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (refs.audioRef.current) { destroyAudio(refs.audioRef.current); refs.audioRef.current = null; }
  }, []);

  return {
    recordingState,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    playRecordedAudio,
    pausePlayback,
    resumePlayback,
    cleanup,
    updateRecordingState,
    getRecordedAudioBlob,
    maxDurationReached,
    maxRecordingDuration: MAX_RECORDING_DURATION,
    resetMaxDurationReached: () => setMaxDurationReached(false),
  };
};
